import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import produce from 'immer';
import { useHistory, useParams } from 'react-router';
import { Formik } from 'formik';
import * as yup from 'yup';

import { FieldInput } from 'components/own';
import { Row, UncontrolledCollapse } from 'reactstrap';
import styled from 'styled-components';
import { propsAreEqual } from 'utils/util';
import { ChevronUpIcon } from 'components/icons';
import { cloneDeep, get, pick } from 'lodash';
import { usePushNotification } from 'hook/useContextSelector';
import { getExpressServices } from 'services/express';
import ActionsItem from 'components/own/ActionsItem';
import { ControlTypes } from 'constants/field';
import { getBranches } from 'services/branch';
import { createExpressRef, getExpressRefById, updateExpressRef } from 'services/express-ref';

const Title = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 30px;

  color: #1f4173;

  margin-bottom: 17px;
`;

const Block = styled.div`
  background-color: #fff;
  margin-bottom: 16px;
  border-radius: 4px;
`;

const BlockHeader = styled.div`
  padding: 0 12px;
  height: 44px;
  border-bottom: 1px solid #e4e4eb;

  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;

  color: #1f4173;
`;

const BlockContent = styled(UncontrolledCollapse)`
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 60px;
  grid-row-gap: 8px;
`;

const IconButton = styled.div`
  cursor: pointer;
`;
const initialData = {
  service_type: '',
  from_branch: '',
  to_branch: '',
  status: true,
  detail: '',
  service_fee: '',
  sync_2_direction: true,
};
const initialState = { data: null, coupon: '', options: {} };

const ServicePriceItem = () => {
  const [{ data, options }, setState] = useState(initialState);
  const { id } = useParams();
  const router = useHistory();
  const pushNotification = usePushNotification();
  useEffect(() => {
    const callAPI = async () => {
      const branch = await getBranches();
      const expressServices = await getExpressServices();
      setState(
        produce(draft => {
          draft.options.branch = branch?.map(_b => ({ label: _b.branch_name, value: get(_b, ['_id', '$oid']) }));
          draft.options.service = expressServices?.map(_b => ({ label: _b.service_detail, value: get(_b, ['_id', '$oid']) }));
        })
      );
    };
    callAPI();
  }, []);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        service_type: yup.string().required('Vui lòng nhập loại dịch vụ.'),
        detail: yup.string(),
        from_branch: yup.string().required('Vui lòng chọn nơi đi.'),
        to_branch: yup.string().required('Vui lòng chọn nơi đến.'),
        service_fee: yup.string().required('Vui lòng nhập giá tiền.'),
        status: yup.boolean(),
      }),
    []
  );

  const fields = useMemo(
    () => [
      {
        id: 'block-1',
        title: 'Thông tin dịch vụ',
        content: [
          {
            name: 'from_branch',
            label: 'Chi nhánh đi',
            controlType: ControlTypes.SELECT_PICKER,
            option: 'branch',
            placeholder: 'Chọn chi nhánh',
          },
          {
            name: 'to_branch',
            label: 'Chi nhánh đến',
            controlType: ControlTypes.SELECT_PICKER,
            option: 'branch',
            placeholder: 'Chọn chi nhánh',
          },
          {
            name: 'service_type',
            label: 'Tên loại hàng',
            isRequired: true,
            controlType: ControlTypes.SELECT_PICKER,
            option: 'service',
            placeholder: 'Chọn dịch vụ',
          },
          {
            label: 'Giá tiền (k)',
            name: 'service_fee',
          },
          {
            label: 'Mô tả',
            name: 'detail',
            type: 'textarea',
          },
          {
            label: 'Active',
            name: 'status',
            controlType: ControlTypes.CHECK_BOX,
          },
        ],
      },
    ],
    []
  );

  const onCancel = useCallback(() => {
    router.replace('/service-price');
  }, [router]);

  const onSave = useCallback(
    values => {
      let request = pick(cloneDeep(values), ['id', 'from_branch', 'to_branch', 'service_type', 'service_fee', 'detail', 'status']);
      if (values.id) {
        updateExpressRef(values.id, request)
          .then(() => {
            pushNotification('success', 'Lưu thành công');
            router.replace('/service-price');
          })
          .catch(error => {
            pushNotification('error', error);
          });
      } else {
        createExpressRef(request)
          .then(() => {
            pushNotification('success', 'Tạo thành công');
            router.replace('/service-price');
          })
          .catch(error => {
            pushNotification('error', error);
          });
      }
    },
    [pushNotification, router]
  );

  useEffect(() => {
    if (id === 'new') {
      setState(
        produce(draft => {
          draft.data = initialData;
        })
      );
    } else
      getExpressRefById(id).then(res => {
        setState(
          produce(draft => {
            draft.data = { ...res, from_branch: res?.from_branch?._id, to_branch: res?.to_branch?._id, service_type: res?.service_type?._id };
          })
        );
      });
  }, [id]);

  if (!data) return <div />;

  return (
    <div className="page-content">
      <div className="d-flex align-items-center">
        <Title>Chi tiết giá</Title>
      </div>
      <Formik initialValues={data} onSubmit={onSave} validationSchema={validationSchema}>
        {({ errors = {}, handleSubmit, handleBlur, handleChange, submitCount, touched = {}, values = {} }) => (
          <>
            {fields.map(block => (
              <Block key={block.id}>
                <BlockHeader className="d-flex align-items-center justify-content-between">
                  {block.title}
                  <IconButton id={block.id}>
                    <ChevronUpIcon />
                  </IconButton>
                </BlockHeader>
                <BlockContent defaultOpen toggler={`#${block.id}`}>
                  {block.content.map(field => (
                    <Row key={field.name}>
                      <FieldInput
                        {...field}
                        error={get(touched, field.name) || !!submitCount ? get(errors, field.name) : null}
                        value={get(values, field.name)}
                        autoComplete="new-password"
                        onBlur={handleBlur}
                        options={options[field.option]}
                        onChange={handleChange}
                        placeholder={field.placeholder || field.label}
                      />
                    </Row>
                  ))}
                  {id === 'new' && (
                    <Row>
                      <FieldInput
                        label="Đồng bộ 2 chi nhánh"
                        error={get(touched, 'sync_2_direction') || !!submitCount ? get(errors, 'sync_2_direction') : null}
                        value={get(values, 'sync_2_direction')}
                        controlType={ControlTypes.CHECK_BOX}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Row>
                  )}
                </BlockContent>
              </Block>
            ))}
            <ActionsItem onSave={handleSubmit} onCancel={onCancel} />
          </>
        )}
      </Formik>
    </div>
  );
};

export default memo(ServicePriceItem, propsAreEqual);
